import * as React from "react";
import styled from "styled-components";
import useSizing from "./useSizing";
import { VariableSizeGrid, GridChildComponentProps } from "react-window";

const MIN_TILE_WIDTH = 225;
const MAX_TILE_WIDTH = 300;
const HEIGHT = 225;

const Base = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  position: relative;
`;

const ResizeListener = styled.div`
  display: block;
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;
`;

const Cell = React.memo(
  function Cell({
    rowIndex,
    columnIndex,
    data: { children, totalColumns, tileWidth },
    style
  }: GridChildComponentProps) {
    const arrIndex = rowIndex * totalColumns + columnIndex;
    const child = children[arrIndex];
    const ref = React.useRef<HTMLDivElement>();

    React.useEffect(() => {
      if (ref.current) {
        ref.current.style.width = tileWidth + "px";
      }
    }, [tileWidth]);

    return (
      <div style={style}>
        {child &&
          React.cloneElement(child, {
            style: {
              ...child.props.style,
              width: tileWidth
            },
            ref
          })}
      </div>
    );
  },
  (prev: GridChildComponentProps, next: GridChildComponentProps) => {
    if (prev.style.width !== next.style.width) {
      return false;
    }
    if (prev.style.height !== next.style.height) {
      return false;
    }
    if (prev.data !== next.data) {
      return false;
    }
    if (prev.style.left !== next.style.left) {
      return false;
    }
    if (prev.style.top !== next.style.top) {
      return false;
    }

    return true;
  }
);

type Props = {
  style?: React.CSSProperties;
  className?: string;
  children: any;
  minMargin: number;
  minTileWidth?: number;
  maxTileWidth?: number;
  tileHeight?: number;
};

function TileContainerErik(
  {
    style,
    className,
    minMargin = 20,
    children,
    minTileWidth = MIN_TILE_WIDTH,
    maxTileWidth = MAX_TILE_WIDTH,
    tileHeight = HEIGHT
  }: Props,
  ref: any
) {
  const {
    dimensions,
    gridRef,
    resizeRef,
    cellHeights,
    cellWidths,
    tileWidth,
    totalColumns,
    totalRows
  } = useSizing({
    minMargin,
    minTileWidth,
    maxTileWidth,
    amountOfChildren: React.Children.count(children),
    tileHeight
  });

  const itemData = React.useMemo(
    () => ({
      children: React.Children.toArray(children),
      totalColumns,
      tileWidth
    }),
    [totalColumns, tileWidth, tileWidth, children]
  );

  return (
    <Base ref={ref} style={style} className={className}>
      <ResizeListener
        style={style}
        ref={resizeRef}
        aria-hidden={true}
        aria-label="resize-listener"
        tabIndex={-1}
      />

      {dimensions && (
        <VariableSizeGrid
          overscanRowsCount={2}
          ref={gridRef}
          columnWidth={index => cellWidths![index]}
          rowHeight={index => cellHeights![index]}
          columnCount={totalColumns!}
          rowCount={totalRows!}
          width={dimensions.width}
          height={dimensions.height}
          itemData={itemData}
        >
          {Cell}
        </VariableSizeGrid>
      )}
    </Base>
  );
}

export default React.forwardRef(TileContainerErik);
